import { Pipe, PipeTransform } from '@angular/core';

/**
 * How long ago a timestamp was, in calendar days, as the browser's own
 * relative-time formatter phrases it — "today", "yesterday", "3 days ago".
 *
 *   Started {{ app.created_at | ago }}
 *
 * FLOW-394 (Henk, 2026-10-22): the block at the top of an unfinished
 * application says how old it is, not a date. Day granularity on purpose — an
 * applicant does not need "4 hours ago", and the day boundary is the viewer's
 * local one (see datetime-timezones: the server sends UTC, the browser
 * converts). The wording and plurals come from `Intl.RelativeTimeFormat`, not
 * from here. Null, undefined or an unparseable value renders as "—".
 */
@Pipe({ name: 'ago', standalone: true, pure: true })
export class AgoPipe implements PipeTransform {
  private readonly format = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  transform(value: string | Date | null | undefined, now: Date = new Date()): string {
    if (value === null || value === undefined || value === '') return '—';
    const then = new Date(value);
    if (Number.isNaN(then.getTime())) return '—';
    const days = Math.round((localMidnight(now) - localMidnight(then)) / 86_400_000);
    return this.format.format(-days, 'day');
  }
}

/** Midnight at the start of `d`'s calendar day, in the viewer's zone. */
function localMidnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
